import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtWeekLabel } from "../lib/format";
import type { WeekAZM } from "../lib/types";

interface WeeklyChartProps {
  rows: WeekAZM[];
  height?: number;
}

export default function WeeklyChart({ rows, height = 220 }: WeeklyChartProps) {
  const data = rows.map((r) => ({
    weekStart: r.weekStart,
    minutes: r.minutes,
    daysCounted: r.daysCounted,
  }));
  const maxY = Math.max(10, ...rows.map((r) => r.minutes)) * 1.1;
  const currentWeek = rows.at(-1)?.weekStart;

  return (
    <div className="-mx-1">
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid
            stroke="var(--grid)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="weekStart"
            tickFormatter={(v) => fmtWeekLabel(v).split(" – ")[0]}
            interval={Math.max(0, Math.floor(rows.length / 8))}
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
            domain={[0, maxY]}
            width={36}
          />
          <Tooltip
            cursor={{ fill: "rgba(120,120,120,0.08)" }}
            content={<WeeklyTooltip />}
          />
          <Bar dataKey="minutes" radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell
                key={d.weekStart}
                fill={
                  d.weekStart === currentWeek
                    ? "var(--accent)"
                    : "var(--accent-soft)"
                }
                stroke="var(--accent)"
                strokeOpacity={d.weekStart === currentWeek ? 1 : 0.45}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TooltipPayload {
  active?: boolean;
  payload?: {
    payload: { weekStart: string; minutes: number; daysCounted: number };
  }[];
}

function WeeklyTooltip({ active, payload }: TooltipPayload) {
  if (!active || !payload?.length) return null;
  const { weekStart, minutes, daysCounted } = payload[0].payload;
  return (
    <div className="card px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{fmtWeekLabel(weekStart)}</div>
      <div className="tnum mt-1 text-zinc-900 dark:text-zinc-100">
        <span className="font-semibold">{minutes}</span>
        <span className="ml-1 text-zinc-500">AZM</span>
      </div>
      <div className="tnum text-zinc-500">{daysCounted}/7 days tracked</div>
    </div>
  );
}
